import { useEffect, useState } from "react";
import { CSVLink } from "react-csv";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";

// Soft UI Dashboard PRO React components
import SoftBox from "src/components/SoftBox";
import SoftButton from "src/components/SoftButton";
import SoftDateRangePicker from "src/components/SoftDateRangePicker";

// Soft UI Dashboard PRO React example components
import MiniStatisticsCard from "src/containers/Cards/StatisticsCards/MiniStatisticsCard";
import DashboardLayout from "src/containers/LayoutContainers/DashboardLayout";
import DashboardNavbar from "src/containers/DashboardNavbar";
import Footer from "src/components/Footer";
import DataTable from "src/components/Tables/DataTable";

import DefaultCell from "src/pages/reports/components/DefaultTableCell";

import { useClubs } from "src/features/club/ClubProvider";
import { formatCurrency } from "src/features/utils";
import {
  format,
  startOfMonth,
  endOfMonth,
  subMonths,
  isWithinInterval,
  startOfDay,
  endOfDay,
  differenceInSeconds,
  subSeconds,
} from "date-fns";
import { query, where, getDocs } from "firebase/firestore";
import { getClubTransactionsCollection } from "src/features/transaction/collection";

const columns = [
  {
    Header: "User",
    accessor: "member.displayName",
    Cell: ({ row: { original } }) => (
      <DefaultCell
        value={
          original?.pilot?.displayName || original?.member?.displayName || "---"
        }
      />
    ),
  },
  {
    Header: "Invoice Date",
    accessor: "createdAt",
    Cell: ({ value }) => (
      <DefaultCell
        value={value?.toDate?.() ? format(value?.toDate?.(), "PP") : "N/A"}
      />
    ),
  },
  {
    Header: "Invoice Amount",
    accessor: "totalCost",
    Cell: ({ value }) => (
      <DefaultCell value={value ? formatCurrency(value) : "---"} />
    ),
  },
  {
    Header: "Total Paid",
    accessor: "payments",
    Cell: ({ value }) => {
      const totalPaid = value.reduce(
        (acc, payment) =>
          payment.status === "succeeded" ? acc + payment.amount / 100 : acc,
        0
      );
      return (
        <DefaultCell value={totalPaid ? formatCurrency(totalPaid) : "---"} />
      );
    },
  },
  {
    Header: "Amount Refunded",
    accessor: "refunded",
    Cell: ({ value }) => <DefaultCell value={formatCurrency(value)} />,
  },
];

function RefundsReport() {
  const {
    selectedClubId,
    selectedClub,
    selectedLocationId,
    selectedLocation,
    clubUsers,
  } = useClubs();
  const [rows, setRows] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [dateRange, setDateRange] = useState([
    startOfMonth(subMonths(new Date(), 1)),
    endOfMonth(subMonths(new Date(), 1)),
  ]);
  const [dateRangePrevious, setDateRangePrevious] = useState([
    startOfMonth(subMonths(new Date(), 2)),
    endOfMonth(subMonths(new Date(), 2)),
  ]);

  const updateDateRange = (dates) => {
    dates[0] = startOfDay(dates[0]);
    dates[1] = endOfDay(dates[1]);

    setDateRange(dates);
  };

  useEffect(() => {
    setDateRangePrevious([
      startOfDay(
        subSeconds(
          dateRange[0],
          differenceInSeconds(dateRange[1], dateRange[0])
        )
      ),
      dateRange[0],
    ]);
  }, [dateRange]);

  const getTransactionData = async () => {
    const transactionDocs = await getDocs(
      query(
        getClubTransactionsCollection(selectedClubId),
        where("location.id", "==", selectedLocationId),
        where("refunded", "!=", null)
      )
    );

    const rowData = transactionDocs.docs
      .filter(
        (transaction) =>
          transaction?.data()?.createdAt &&
          isWithinInterval(transaction.data().createdAt.toDate(), {
            start: dateRange[0],
            end: dateRange[1],
          })
      )
      .map((transactionData) => transactionData.data());

    rowData.sort((a, b) => b.createdAt - a.createdAt);

    const csvDataFromRows = rowData.map((row) => ({
      Name: row.pilot?.displayName || row.member?.displayName,
      Date: row?.createdAt?.toDate?.()
        ? format(row.createdAt.toDate(), "PP")
        : "N/A",
      "Invoice Amount": formatCurrency(row.totalCost),
      "Total Paid": formatCurrency(
        row.payments.reduce(
          (acc, payment) =>
            payment.status === "succeeded" ? acc + payment.amount / 100 : acc,
          0
        )
      ),
      "Amount Refunded": formatCurrency(row.refunded),
    }));

    setCsvData(csvDataFromRows);

    setRows(rowData);
  };

  useEffect(() => {
    if (!clubUsers) return;
    getTransactionData();
  }, [dateRange, dateRangePrevious, selectedClubId, selectedLocationId]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
        >
          <SoftBox />
          <SoftBox display="flex">
            <SoftDateRangePicker
              value={dateRange}
              onChange={(newDates) => updateDateRange(newDates)}
            />
            <SoftBox mx={1}>
              <Divider orientation="vertical" />
            </SoftBox>
            <SoftBox>
              <CSVLink
                data={csvData}
                filename={`${selectedClub.name}-${
                  selectedLocation?.icao
                }-check_ins-${format(new Date(), "MM/dd/yyyy")}.csv`}
                target="_blank"
              >
                <SoftButton variant="outlined" color="dark">
                  <Icon>description</Icon>
                  &nbsp;export csv
                </SoftButton>
              </CSVLink>
            </SoftBox>
          </SoftBox>
        </SoftBox>
        <SoftBox my={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <MiniStatisticsCard
                title={{
                  text: "Number of Refunds",
                  fontWeight: "medium",
                }}
                count={rows.length}
                icon={{
                  color: "info",
                  component: "person",
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MiniStatisticsCard
                title={{
                  text: "Total Refunded",
                  fontWeight: "medium",
                }}
                count={formatCurrency(
                  rows.reduce((acc, row) => acc + row.refunded, 0)
                )}
                icon={{
                  color: "info",
                  component: "account_balance",
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MiniStatisticsCard
                title={{
                  text: "Total Billed",
                  fontWeight: "medium",
                }}
                count={formatCurrency(
                  rows.reduce((acc, row) => acc + row.totalCost, 0)
                )}
                icon={{
                  color: "info",
                  component: "currency_exchange",
                }}
              />
            </Grid>
          </Grid>
        </SoftBox>
        <Card>
          <DataTable
            table={{ columns, rows }}
            entriesPerPage={{ defaultValue: 10, entries: [10, 20, 50, 100] }}
            canSearch
          />
        </Card>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default RefundsReport;
